import { Typography } from '@mui/material';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CrudTable,
  DataGridColumnDefinition,
  StylizedNumber,
} from 'components';
import { TranslationNamespace } from 'i18n';
import {
  PayinBankPairRow,
  PayinLossPoints,
  PayinPaymentTypeRow,
  PayinRequisitesKindRow,
} from 'types';
import { payinProviderStatisticsUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PayinLossPoints>;
};

const EMPTY_VALUE = '—';

export const LossPointsBlock: React.FC<Props> = ({ queryResult }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion',
  });

  const requisitesKindsQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.requisitesKinds,
      } as UseQueryResult<PayinRequisitesKindRow[]>),
    [queryResult],
  );

  const bankPairsQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.bankPairs,
      } as UseQueryResult<PayinBankPairRow[]>),
    [queryResult],
  );

  const paymentTypesQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.paymentTypes,
      } as UseQueryResult<PayinPaymentTypeRow[]>),
    [queryResult],
  );

  const requisitesKindsColumns = useMemo(
    (): DataGridColumnDefinition<PayinRequisitesKindRow>[] => [
      {
        header: t('loss_points.requisites_kinds.columns.provider'),
        valueGetter: (item) => item.providerName || EMPTY_VALUE,
      },
      {
        header: t('loss_points.requisites_kinds.columns.kind'),
        valueGetter: (item) =>
          payinProviderStatisticsUtils.getRequisitesKindLabel(
            item.requisitesKind,
          ),
      },
      {
        header: t('loss_points.requisites_kinds.columns.country'),
        valueGetter: (item) => item.countryCode || EMPTY_VALUE,
      },
      {
        header: t('loss_points.requisites_kinds.columns.assigned'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.assignedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('loss_points.requisites_kinds.columns.completed'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.completedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('loss_points.requisites_kinds.columns.conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.conversion} unit="%" />
        ),
      },
    ],
    [t, tCommon],
  );

  const bankPairsColumns = useMemo(
    (): DataGridColumnDefinition<PayinBankPairRow>[] => [
      {
        header: t('loss_points.bank_pairs.columns.customer_bank'),
        valueGetter: (item) => item.customerBankName || EMPTY_VALUE,
      },
      {
        header: t('loss_points.bank_pairs.columns.provided_bank'),
        valueGetter: (item) => item.providedBankName || EMPTY_VALUE,
      },
      {
        header: t('loss_points.bank_pairs.columns.country'),
        valueGetter: (item) => item.countryCode || EMPTY_VALUE,
      },
      {
        header: t('loss_points.bank_pairs.columns.assigned'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.assignedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('loss_points.bank_pairs.columns.completed'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.completedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('loss_points.bank_pairs.columns.conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.conversion} unit="%" />
        ),
      },
    ],
    [t, tCommon],
  );

  const paymentTypesColumns = useMemo(
    (): DataGridColumnDefinition<PayinPaymentTypeRow>[] => [
      {
        header: t('loss_points.payment_types.columns.payment_type'),
        valueGetter: (item) => item.paymentTypeName || EMPTY_VALUE,
      },
      {
        header: t('loss_points.payment_types.columns.assigned'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.assignedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('loss_points.payment_types.columns.completed'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.completedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('loss_points.payment_types.columns.conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.conversion} unit="%" />
        ),
      },
    ],
    [t, tCommon],
  );

  return (
    <Fragment>
      <Typography variant="h6" sx={{ mt: 6, mb: 2 }}>
        {t('loss_points.title')}
      </Typography>
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        {t('loss_points.requisites_kinds.title')}
      </Typography>
      <CrudTable
        queryResult={requisitesKindsQueryResult}
        columns={requisitesKindsColumns}
      />
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        {t('loss_points.bank_pairs.title')}
      </Typography>
      <CrudTable
        queryResult={bankPairsQueryResult}
        columns={bankPairsColumns}
      />
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        {t('loss_points.payment_types.title')}
      </Typography>
      <CrudTable
        queryResult={paymentTypesQueryResult}
        columns={paymentTypesColumns}
      />
    </Fragment>
  );
};
